/**
 * AgentResultRow — one external SEARCH PREVIEW from the agent stream.
 *
 * Deliberately distinct from the talent-pool match row (TalentRow + MatchBreakdown):
 * a preview carries NO score (no skills/education are revealed before unlock). It
 * shows identity + provenance + a LinkedIn link, and a per-row "Unlock" action
 * that reveals the full profile (a paid lookup) and adds it to the talent pool.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Linkedin, RotateCw, Sparkles } from 'lucide-react';

import { Button } from '../../atoms/Button';
import { Tag } from '../../atoms/Tag';
import { TalentRow } from '@/components/talent-pool/TalentRow';

import type { AgentCandidate } from './types';

interface AgentResultRowProps {
  candidate: AgentCandidate;
  onUnlock: (candidate: AgentCandidate) => void;
}

export const AgentResultRow: React.FC<AgentResultRowProps> = ({ candidate, onUnlock }) => {
  const { t } = useTranslation('agents');
  const status = candidate.unlock?.status ?? 'locked';

  // "Title · Employer" on one line; location on a quieter line below.
  const secondary = [candidate.title, candidate.employer].filter(Boolean).join(' · ') || undefined;

  return (
    <div className="border-b border-light-lila/30 last:border-0">
      <div className="flex items-start gap-3 px-3 py-3">
        <div className="flex-1 min-w-0">
          <TalentRow
            fullName={candidate.fullName ?? candidate.initials}
            initials={candidate.initials}
            avatarUrl={candidate.avatarUrl ?? undefined}
            secondary={secondary}
            avatarSize="sm"
          />
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 pl-[2.75rem] text-xs text-dark-invirtus/55">
            {candidate.location && <span>{candidate.location}</span>}
            {candidate.linkedinUrl && (
              <a
                href={candidate.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#0A66C2] hover:underline"
              >
                <Linkedin className="h-3 w-3" />
                {t('sourcing.view_linkedin')}
              </a>
            )}
          </div>
        </div>

        <div className="shrink-0 self-center">
          <UnlockAction status={status} onUnlock={() => onUnlock(candidate)} />
        </div>
      </div>
    </div>
  );
};

const UnlockAction: React.FC<{ status: string; onUnlock: () => void }> = ({ status, onUnlock }) => {
  const { t } = useTranslation('agents');

  if (status === 'unlocked') {
    return (
      <Tag intent="success" variant="subtle" icon={<Check className="h-3.5 w-3.5" />}>
        {t('sourcing.unlocked')}
      </Tag>
    );
  }

  if (status === 'unlocking') {
    return (
      <Button type="outlined" intent="primary" size="sm" loading disabled label={t('sourcing.unlocking')} />
    );
  }

  if (status === 'error') {
    return (
      <Button
        type="outlined"
        intent="danger"
        size="sm"
        icon={<RotateCw/>}
        label={t('sourcing.unlock_retry')}
        onClick={onUnlock}
      />
    );
  }

  return (
    <Button
      type="outlined"
      intent="primary"
      size="sm"
      icon={<Sparkles/>}
      label={t('sourcing.unlock')}
      onClick={onUnlock}
    />
  );
};

AgentResultRow.displayName = 'AgentResultRow';
